import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  IconButton,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * ModuleManagementDialog Component
 * Dialog for creating and editing modules
 */
const ModuleManagementDialog = ({
  open,
  onClose,
  module = null,
  onSave,
  mode = 'create', // 'create' or 'edit'
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: '',
    category: 'Other',
  });
  const [errors, setErrors] = useState({});

  // Categories available for modules
  const categories = [
    'Sales',
    'Finance',
    'Inventory',
    'Reports',
    'Settings',
    'Users',
    'Other',
  ];

  // Reset form when dialog opens or module changes
  useEffect(() => {
    if (module && mode === 'edit') {
      setFormData({
        name: module.ModuleName || module.name || '',
        displayName: module.DisplayName || module.displayName || module.ModuleName || module.name || '',
        description: module.Description || module.description || '',
        category: module.Category || module.category || 'Other',
      });
    } else {
      setFormData({
        name: '',
        displayName: '',
        description: '',
        category: 'Other',
      });
    }
    setErrors({});
  }, [module, mode, open]);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Clear error for this field
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: '',
      });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('access_management.module_name_required');
    } else if (formData.name.length < 3) {
      newErrors.name = t('access_management.module_name_too_short');
    }

    if (!formData.category) {
      newErrors.category = t('access_management.category_required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }

    onSave({
      ...formData,
      id: module?.id,
    });
  };

  const handleClose = () => {
    setFormData({
      name: '',
      displayName: '',
      description: '',
      category: 'Other',
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {mode === 'create' ? <AddIcon /> : <EditIcon />}
            <Typography variant="h6">
              {mode === 'create' 
                ? t('access_management.create_module') 
                : t('access_management.edit_module')}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 1 }}>
          {mode === 'create' && (
            <Alert severity="info">
              {t('access_management.module_creation_info')}
            </Alert>
          )}

          {/* Module Name */}
          <TextField
            label={t('access_management.module_name')}
            value={formData.name}
            onChange={handleChange('name')}
            error={!!errors.name}
            helperText={errors.name || t('access_management.module_name_help')}
            required
            fullWidth
            autoFocus
            placeholder="e.g., Customers, Products, Reports"
          />

          {/* Display Name */}
          <TextField
            label={t('access_management.display_name')}
            value={formData.displayName}
            onChange={handleChange('displayName')}
            error={!!errors.displayName}
            helperText={errors.displayName || t('access_management.display_name_help')}
            fullWidth
            placeholder="Leave empty to use Module Name"
          />

          {/* Category */}
          <TextField
            select
            label={t('access_management.category')}
            value={formData.category}
            onChange={handleChange('category')}
            error={!!errors.category}
            helperText={errors.category || t('access_management.category_help')}
            required
            fullWidth
          >
            {categories.map((category) => (
              <MenuItem key={category} value={category}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {t(`access_management.category_${category.toLowerCase()}`)}
                  <Chip 
                    label={category} 
                    size="small" 
                    sx={{ fontSize: '0.7rem' }}
                  />
                </Box>
              </MenuItem>
            ))}
          </TextField>

          {/* Description */}
          <TextField
            label={t('access_management.description')}
            value={formData.description}
            onChange={handleChange('description')}
            multiline
            rows={3}
            fullWidth
            helperText={t('access_management.description_help')}
          />

          {/* Preview */}
          <Box
            sx={{
              p: 2,
              bgcolor: '#f5f5f5',
              borderRadius: 1,
              border: '1px solid #e0e0e0',
            }}
          >
            <Typography variant="caption" color="text.secondary" gutterBottom>
              {t('access_management.preview')}
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {formData.name || t('access_management.module_name_placeholder')}
            </Typography>
            {formData.displayName && formData.displayName !== formData.name && (
              <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                Display: {formData.displayName}
              </Typography>
            )}
            {formData.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {formData.description}
              </Typography>
            )}
            <Chip 
              label={formData.category} 
              size="small" 
              sx={{ mt: 1 }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined">
          {t('cancel')}
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          }}
        >
          {mode === 'create' ? t('create') : t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModuleManagementDialog;
